import { Button } from '@/components/ui/button';
import { CurrencyInput } from '@/components/ui/currency-input';
import { DateSelectorPopover } from '@/components/ui/date-selector/date-selector-popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from '@/hooks/use-toast';
import useUsers from '@/hooks/use-users';
import { centsToInitialValue, currencyInputValueToCents } from '@/lib/currency';
import { checkHotkey, HOTKEYS } from '@/lib/hotkeys';
import { pdkAxios } from '@/lib/pdk-axios';
import { cn } from '@/lib/utils';
import { dealStore } from '@/models/stores/deals';
import type { Payment } from '@/models/types';
import { useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';
import { FORMAT_DATE_STRING } from './add-payment';
import { PaymentEntireOrPartialSelect } from './payment-entire-or-partial-select';
import { PaymentMethodSelectorCombobox } from './payment-method-selector-combobox';
import { PaymentTypeSelectorCombobox } from './payment-type-selector-combobox';

const ROW_CLASSNAME = 'flex flex-row gap-2 items-center justify-between';

export type EditPaymentDialogProps = {
  dealId: string;
  payment: Payment;
  createdById: string;
  ctaText: string;
  onOpen: () => void;
  onClose: () => void;
  dialogIsOpen: boolean;
  children?: React.ReactNode;
  onSubmit: () => void;
};

const EditPaymentDialog = observer(
  ({
    dealId,
    payment,
    createdById,
    ctaText,
    onOpen,
    onClose,
    dialogIsOpen,
    children,
    onSubmit,
  }: EditPaymentDialogProps) => {
    const [amountValue, setAmountValue] = useState(
      centsToInitialValue(payment.amount),
    );
    const [intendedPaymentDateValue, setIntendedPaymentDateValue] = useState(
      payment.intent_date,
    );
    const [paymentSentDateValue, setPaymentSentDateValue] = useState(
      payment.sent_date,
    );
    const [paymentReceivedDateValue, setPaymentReceivedDateValue] = useState(
      payment.received_date,
    );
    const [paymentRefundedDateValue, setPaymentRefundedDateValue] = useState(
      payment.refunded_date,
    );
    const [methodValue, setMethodValue] = useState<Payment['method']>(
      payment.method,
    );
    const [typeValue, setTypeValue] = useState<Payment['type']>(payment.type);
    const [isFullPaymentValue, setIsFullPaymentValue] = useState(false);

    useEffect(() => {
      if (dialogIsOpen && payment) {
        setAmountValue(centsToInitialValue(payment.amount));
        setIntendedPaymentDateValue(payment.intent_date);
        setPaymentSentDateValue(payment.sent_date);
        setPaymentReceivedDateValue(payment.received_date);
        setPaymentRefundedDateValue(payment.refunded_date);
        setMethodValue(payment.method);
        setTypeValue(payment.type);
        setIsFullPaymentValue(payment.is_full_payment ?? false);
      }
    }, [dialogIsOpen, payment.id]);

    const { findUserById } = useUsers();
    const createdByUser = findUserById(createdById);

    const updatePaymentMutation = useMutation({
      onMutate: async ({ payment }: { payment: Payment }) => {
        dealStore.upsertPayment({
          ...payment,
          deal_id: dealId,
        });
      },
      mutationFn: async ({ payment }: { payment: Payment }) => {
        const response = await pdkAxios
          .post(`/deals/${dealId}/payments/${payment.id}/update`, payment)
          .catch((e) => {
            console.error('Error updating payment', e);
            throw e;
          });

        return response.data;
      },
      onSuccess: () => {},
      onError: (error, variables) => {
        console.error('Error updating payment', error, variables);
      },
      onSettled: () => {
        // queryClient.invalidateQueries({ queryKey: ["deal", shortId] });
      },
    });

    const handleUpdatePayment = useCallback(
      (newPayment: Payment) => {
        updatePaymentMutation.mutate({
          payment: {
            ...newPayment,
          },
        });
      },
      [updatePaymentMutation],
    );

    const handleSubmit = useCallback(() => {
      if (!intendedPaymentDateValue) {
        toast({
          title: 'Intended payment date is required',
          description: 'Please enter an intended payment date',
        });
        return;
      }

      const amountCents = currencyInputValueToCents(amountValue);

      if (amountCents === 0 || amountCents < 0) {
        toast({
          title: 'Amount is invalid',
          description: 'Please enter a positive amount',
        });
        return;
      }

      handleUpdatePayment({
        ...payment,
        amount: amountCents,
        intent_date: dayjs(intendedPaymentDateValue).format(FORMAT_DATE_STRING),
        sent_date: paymentSentDateValue
          ? dayjs(paymentSentDateValue).format(FORMAT_DATE_STRING)
          : null,
        received_date: paymentReceivedDateValue
          ? dayjs(paymentReceivedDateValue).format(FORMAT_DATE_STRING)
          : null,
        refunded_date: paymentRefundedDateValue
          ? dayjs(paymentRefundedDateValue).format(FORMAT_DATE_STRING)
          : null,
        method: methodValue,
        type: typeValue,
        is_full_payment: isFullPaymentValue,
      });
      onSubmit?.();
    }, [
      amountValue,
      intendedPaymentDateValue,
      paymentSentDateValue,
      paymentReceivedDateValue,
      paymentRefundedDateValue,
      methodValue,
      typeValue,
      isFullPaymentValue,
      onSubmit,
      payment,
      handleUpdatePayment,
    ]);

    return (
      <Dialog open={dialogIsOpen}>
        <DialogTrigger asChild onClick={onOpen}>
          {children}
        </DialogTrigger>
        <DialogContent
          className="sm:max-w-[425px]"
          onClickClose={onClose}
          onPointerDownOutside={(e) => {
            e.preventDefault();
          }}
          onKeyDown={(e) => {
            if (
              checkHotkey({
                hotkey: HOTKEYS.PAYMENT_DIALOG_SUBMIT as string,
                event: e,
              })
            ) {
              handleSubmit();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>Update payment</DialogTitle>
            <DialogDescription>
              Created by {createdByUser?.first_name} {createdByUser?.last_name}
            </DialogDescription>
          </DialogHeader>
          <div className={cn(ROW_CLASSNAME)}>
            <label className="text-sm">Amount</label>
            <CurrencyInput
              className="rounded text-xs text-right"
              placeholder="Add Amount"
              value={amountValue}
              onChange={(e) => setAmountValue(e.target.value)}
            />
          </div>
          <div className={cn(ROW_CLASSNAME)}>
            <label className="text-sm">Intended Payment Date</label>
            <div className="flex flex-row gap-3 items-center">
              <DateSelectorPopover
                date={intendedPaymentDateValue}
                onSelectDate={(date) => {
                  setIntendedPaymentDateValue(date?.toISOString() ?? '');
                }}
              />
            </div>
          </div>
          <div className={cn(ROW_CLASSNAME)}>
            <label className="text-sm">Payment Sent Date</label>
            <div className="flex flex-row gap-3 items-center">
              <DateSelectorPopover
                date={paymentSentDateValue}
                onSelectDate={(date) => {
                  setPaymentSentDateValue(date?.toISOString() ?? '');
                }}
              ></DateSelectorPopover>
            </div>
          </div>
          <div className={cn(ROW_CLASSNAME)}>
            <label className="text-sm">Payment Received Date</label>
            <div className="flex flex-row gap-3 items-center">
              <DateSelectorPopover
                date={paymentReceivedDateValue}
                onSelectDate={(date) => {
                  setPaymentReceivedDateValue(date?.toISOString() ?? '');
                }}
              />
            </div>
          </div>

          <div className={cn(ROW_CLASSNAME)}>
            <label className="text-sm">Method</label>
            <PaymentMethodSelectorCombobox
              value={methodValue}
              setValue={(value) => setMethodValue(value)}
            />
          </div>
          <div className={cn(ROW_CLASSNAME)}>
            <label className="text-sm">Type</label>
            <PaymentTypeSelectorCombobox
              value={typeValue}
              setValue={(value) => setTypeValue(value)}
            />
          </div>
          <div className={cn(ROW_CLASSNAME)}>
            <label className="text-sm">Entire Payment</label>
            <PaymentEntireOrPartialSelect
              value={isFullPaymentValue ? 'yes' : 'no'}
              setValue={(value) => setIsFullPaymentValue(value === 'yes')}
            />
          </div>

          <div className={cn(ROW_CLASSNAME)}>
            <label className="text-sm">Payment Refunded Date</label>
            <div className="flex flex-row gap-3 items-center">
              <DateSelectorPopover
                date={paymentRefundedDateValue}
                onSelectDate={(date) => {
                  setPaymentRefundedDateValue(date?.toISOString() ?? '');
                }}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="submit" onClick={handleSubmit}>
              {ctaText}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  },
);

export default EditPaymentDialog;
